import {
  Button,
  Collapse,
  IconButton,
  TableRow as MuiTableRow,
  SxProps,
  TableCell,
  TableRowProps,
  Theme,
  styled,
  useTheme,
} from '@mui/material';
import { ChevronDown, ChevronUp, Pen } from 'lucide-react';
import React from 'react';
import ListCell from './list/list-cell';

const PREFIX = 'ListRow';
const TABLE_PREFIX = 'BodyTable';

const classes = {
  tableRow: `${PREFIX}-tableRow`,
  tableRowExpanded: `${PREFIX}-tableRowExpanded`,
  tableRowNoHover: `${PREFIX}-tableRowNoHover`,
  tableRowHover: `${PREFIX}-tableRowHover`,

  tableHeadCell: `${TABLE_PREFIX}-tableHeadCell`,
  autoWidth: `${TABLE_PREFIX}-autoWidth`,
  fullWidth: `${TABLE_PREFIX}-fullWidth`,
  sticky: `${TABLE_PREFIX}-sticky`,
};

const sharedValues = {
  color: 'inherit',
  display: 'table-row',
  verticalAlign: 'middle',
} as const;

const borderRadius = 8;

const TableRow = styled(MuiTableRow)(({ theme }) => {
  return {
    backgroundColor: 'white',
    ...sharedValues,
    '& td:first-of-type': {
      paddingLeft: 16,
    },
    '& td:last-of-type': {
      paddingRight: 16,
    },
    [`&.${classes.tableRow}`]: {
      borderRadius: borderRadius, // match td child radius
      '& td:first-of-type': {
        borderTopLeftRadius: borderRadius,
        borderBottomLeftRadius: borderRadius,
      },
      '& td:last-of-type': {
        borderTopRightRadius: borderRadius,
        borderBottomRightRadius: borderRadius,
      },
    },
    [`&.${classes.tableRowExpanded}`]: {
      '& td:first-of-type': {
        borderTopLeftRadius: borderRadius,
      },
      '& td:last-of-type': {
        borderTopRightRadius: borderRadius,
      },
    },
  };
});

const BodyTableCell = styled(TableCell)(({ theme }) => {
  return {
    paddingBottom: 0,
    paddingTop: 0,
    backgroundColor: 'white',
    borderBottomLeftRadius: borderRadius,
    borderBottomRightRadius: borderRadius,
  };
});

type Props = TableRowProps & {
  body?: React.ReactNode;
  openBody?: boolean;
  colSpan?: number;
  onClick?: () => void | undefined;
  bodyOnClick?: () => void | undefined;
  sx?: SxProps<Theme>;
};

const NewListRow: React.FC<Props> = ({ children, body, openBody = false, colSpan, onClick, bodyOnClick, sx }) => {
  const [expanded, setExpanded] = React.useState<boolean>(openBody);
  const theme = useTheme();

  const handleOnClick = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    const target = event.target as HTMLElement;
    if (target.parentNode === event.currentTarget) {
      event.preventDefault();
      event.stopPropagation();
      if (!!body) {
        setExpanded((prev) => !prev);
        if (bodyOnClick) bodyOnClick();
      } else if (onClick) onClick();
    }
  };

  return (
    <React.Fragment>
      <TableRow
        classes={{
          root: expanded ? classes.tableRowExpanded : classes.tableRow,
        }}
        sx={{
          '& > *': { borderBottom: 'unset' },
          ...sx,
        }}
        onClick={handleOnClick}>
        {children}
        {!!body && (
          <ListCell align="center">
            <IconButton
              sx={{ color: theme.palette.darkBlue.main }}
              disableRipple
              aria-label="expand row"
              size="small"
              onClick={() => {
                setExpanded(!expanded);
                if (bodyOnClick) bodyOnClick();
              }}>
              {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </IconButton>
          </ListCell>
        )}
      </TableRow>
      {!!body && (
        <TableRow>
          <BodyTableCell colSpan={colSpan}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              {body}
            </Collapse>
          </BodyTableCell>
        </TableRow>
      )}
      <TableRow sx={{ height: 8 }} />
    </React.Fragment>
  );
};

export default React.memo(NewListRow);
